import { Field, FieldContent, FieldError, FieldLabel } from '@/components/ui/field';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { rsvpNewSpouseFieldsSchema, type AttendeeFields } from '@mr-website/schemas';
import type { LinkedSpouse } from './types';
import { AttendeeFieldsForm } from './AttendeeFieldsForm';
import { formatFullName, spouseHasOwnAccount, type FieldErrors } from './utils';

const SPOUSE_EMAIL_TOOLTIP =
  'If you would like to change the email on file, please contact your advisor';

export interface SpouseFormState {
  enabled: boolean;
  fields: AttendeeFields;
}

interface SpouseSectionProps {
  linkedSpouse: LinkedSpouse | null;
  spouse: SpouseFormState;
  errors?: FieldErrors;
  canEnable?: boolean;
  onChange: (next: SpouseFormState) => void;
  onFieldBlur?: (field: keyof AttendeeFields, values: AttendeeFields) => void;
}

export function SpouseSection({
  linkedSpouse,
  spouse,
  errors = {},
  canEnable = true,
  onChange,
  onFieldBlur,
}: SpouseSectionProps) {
  const isLinked = linkedSpouse !== null;
  const spouseOwnsAccount = spouseHasOwnAccount(linkedSpouse);
  const toggleId = 'rsvp-spouse-toggle';

  const toggleLabel = isLinked
    ? `My Spouse — ${formatFullName(linkedSpouse.first_name, linkedSpouse.last_name)}`
    : 'My Spouse';

  function handleToggle(enabled: boolean) {
    if (!enabled) {
      onChange({
        enabled: false,
        fields: createDefaultSpouseFields(linkedSpouse),
      });
      return;
    }

    onChange({
      enabled: true,
      fields: createDefaultSpouseFields(linkedSpouse),
    });
  }

  return (
    <section className="space-y-3">
      <div className="flex items-start gap-2">
        <input
          id={toggleId}
          type="checkbox"
          className="mt-1 size-4 rounded border border-input accent-mr-teal-dark disabled:opacity-50"
          checked={spouse.enabled}
          disabled={!canEnable && !spouse.enabled}
          onChange={(e) => handleToggle(e.target.checked)}
        />
        <Label htmlFor={toggleId} className="font-medium leading-snug text-foreground">
          {toggleLabel}
        </Label>
      </div>

      {spouse.enabled ? (
        <div className="rounded-lg border border-border bg-muted/20 p-4">
          {isLinked && spouseOwnsAccount ? (
            <p className="mb-3 text-xs text-muted-foreground">
              This Spouse / Partner has their own account. Details can only be updated by them or an
              Advisor.
            </p>
          ) : null}

          {isLinked ? (
            <Field className="mb-3">
              <FieldLabel>Email on file</FieldLabel>
              <FieldContent>
                <TooltipProvider delay={200}>
                  <Tooltip>
                    <TooltipTrigger render={<span className="inline-block max-w-full" />}>
                      <p className="cursor-default truncate text-sm text-muted-foreground">
                        {linkedSpouse.email || '—'}
                      </p>
                    </TooltipTrigger>
                    <TooltipContent side="top">{SPOUSE_EMAIL_TOOLTIP}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {errors.email ? <FieldError errors={[{ message: errors.email }]} /> : null}
              </FieldContent>
            </Field>
          ) : null}

          <AttendeeFieldsForm
            idPrefix="spouse"
            values={spouse.fields}
            errors={errors}
            readOnly={isLinked && spouseOwnsAccount}
            hideNames={isLinked && !spouseOwnsAccount}
            hideEmailField={isLinked && !spouseOwnsAccount}
            emailRequired={!isLinked}
            onChange={(fields) => onChange({ ...spouse, fields })}
            onFieldBlur={onFieldBlur}
          />
        </div>
      ) : null}
    </section>
  );
}

export function createDefaultSpouseFields(linkedSpouse: LinkedSpouse | null): AttendeeFields {
  if (!linkedSpouse) {
    return {
      first_name: '',
      last_name: '',
      send_invite: false,
    };
  }

  return {
    first_name: linkedSpouse.first_name,
    last_name: linkedSpouse.last_name,
    send_invite: false,
  };
}

export function createInitialSpouseState(linkedSpouse: LinkedSpouse | null): SpouseFormState {
  return {
    enabled: false,
    fields: createDefaultSpouseFields(linkedSpouse),
  };
}

export function validateSpouse(
  linkedSpouse: LinkedSpouse | null,
  spouse: SpouseFormState,
): FieldErrors {
  if (!spouse.enabled) return {};

  // Variants B/C: spouse on file — reference CRM contact by id; email stays on file.
  if (linkedSpouse) {
    if (!linkedSpouse.contact_id?.trim()) {
      return {
        email: 'Your spouse could not be linked. Please contact your advisor.',
      };
    }
    return {};
  }

  // Variant A: no spouse on file — validate name/email here; CRM contact is created via
  // POST /profile/spouse during RSVP submission before the RSVP payload is sent.
  const result = rsvpNewSpouseFieldsSchema.safeParse(spouse.fields);

  if (result.success) return {};

  const errors: FieldErrors = {};
  for (const issue of result.error.issues) {
    const key = issue.path[0];
    if (typeof key === 'string' && key !== 'send_invite' && !errors[key as keyof AttendeeFields]) {
      errors[key as keyof AttendeeFields] = issue.message;
    }
  }
  return errors;
}
